import { AxiosError } from "axios";
import { FCC, TOption } from "@/types/common";
import { UseVisibleReturn } from "@/hooks/useVisible";
import { Box, CircularProgress, Modal, Stack, styled, Typography } from "@mui/material";
import { background, black, divider, text } from "@/theme/colors";
import { IconBox } from "@/components/elements/IconBox";
import { IconClose, IconCompanyListImport } from "@/assets/icons";
import { rounded } from "@/theme/variables";
import { Combobox } from "@/components/elements/Combobox";
import { DropZone } from "@/components/elements/DropZone";
import { Button } from "@/components/elements/Button";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useMemo, useState } from "react";
import { TExtendFile } from "@/types/file";
import { downloadSample, validateFileFormat, headerToKey } from "@/features/job/utils/excel";
import { FILE_INCORRECT } from "@/features/company/constants/excel";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { validateImportJobApi } from "@/api/services/main/job";
import { TJobRowExcel } from "@/features/job/types/job-excel";
import { AppRoutes } from "@/routes/config";
import { useGetCompanySpecificIndustryList, useGetBusinessClassificationList } from "@/api/services/global/master";
import { useCompanyList } from "@/api/services/main/company";
import { useEnum } from "@/api/services/global/enum";
import { useTagOptions } from "@/api/services/main/tag";
import { useGetMatchInfo } from "@/api/services/main/match";
import { useGetUserListPersonInCharge } from "@/api/services/main/user";
import { sanitizeInput } from "@/utils/pieces";
import Papa from "papaparse";
import chardet from "chardet";
import iconv from "iconv-lite";
import { FileRejection } from "react-dropzone";
import { getExtensionsOfMimeTypes, TFileRules } from "@/utils/file";
import { toast } from "@/utils/toast";

const MAX_LENGTH_ROWS = 210;
const MAX_SIZE = 10 * 1000 * 1000;

type TImportJobModalProps = {
  modal: UseVisibleReturn;
  isLoading?: boolean;
};

type TErrorItem = {
  message: string;
};

type FileType = "xlsx" | "csv";

export const ImportJobModal: FCC<TImportJobModalProps> = ({ modal, isLoading }) => {
  const navigate = useNavigate();
  const DEFAULT_COMPANY_OPTION: TOption = { label: "企業名の列から取得", value: "__default__" };
  const [isSubmit, setIsSubmit] = useState(false);
  const [file, setFile] = useState<File | null>();
  const [errorList, setErrorList] = useState<string[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<TOption | null>(DEFAULT_COMPANY_OPTION);
  const [companyError, setCompanyError] = useState("");

  // Company list for pre-selection
  const { companyOptions, isLoading: isLoadingCompanies } = useCompanyList({ isAll: "1" });
  const companyOptionsWithDefault = [DEFAULT_COMPANY_OPTION, ...companyOptions];

  // Master data & enum hooks for template download dropdowns (単一選択 fields only)
  const { companySpecificIndustryOptions } = useGetCompanySpecificIndustryList();
  const { businessClassificationOptions } = useGetBusinessClassificationList();
  const { data: jobStatusOptions } = useEnum("EJobStatus");
  const { data: jobTagOptions } = useTagOptions("job");
  const { data: employmentTypeOptions } = useEnum("EEmploymentType");
  const { data: salaryTypeOptions } = useEnum("ESalaryType");
  const { data: yesNoPendingOptions } = useEnum("EYesNoPendingStatus");
  const { data: japaneseLevelTypeOptions } = useEnum("EJapaneseLevelType");
  const { data: recruitmentLocationTypeOptions } = useEnum("ERecruitmentLocationType");

  // Master data for 紹介事業所名 and 求人担当者 dropdowns in template
  const { data: matchInfoResponse } = useGetMatchInfo();
  const referralOfficeOptions = useMemo(() => {
    const matchOffices = matchInfoResponse?.data?.matchOffices;
    if (!matchOffices || !Array.isArray(matchOffices)) return [];
    return matchOffices
      .filter((o) => o?.id && o?.officeName?.trim())
      .sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
      .map((o) => ({ label: o.officeName!, value: o.id! }));
  }, [matchInfoResponse]);

  const { userOptions: recruiterOptions } = useGetUserListPersonInCharge();

  const rules: TFileRules = {
    acceptFileTypesExtension: [".csv", ".xlsm", ".xlsx"],
    acceptMimeTypes: [
      "text/csv",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel.sheet.macroEnabled.12",
    ],
  };

  const handleErrorImport = (errorContainer: unknown) => {
    if (typeof errorContainer === "object" && errorContainer !== null) {
      if ("error" in errorContainer) {
        const listErrors = (errorContainer as { error: unknown }).error;
        if (Array.isArray(listErrors)) {
          setErrorList(listErrors.map((item: TErrorItem) => item.message));
        }
      } else if ("message" in errorContainer) {
        setErrorList([(errorContainer as { message: unknown }).message as string]);
      }
    } else {
      setErrorList(["Unknown error"]);
    }
  };

  const onClose = () => {
    setIsSubmit(false);
    setFile(null);
    setErrorList([]);
    setSelectedCompany(DEFAULT_COMPANY_OPTION);
    setCompanyError("");
    modal.close();
  };

  const handleChangeFiles = async (value: TExtendFile | TExtendFile[] | null) => {
    const files = [value ?? []].flat().filter(Boolean);
    if (!files[0]) {
      setFile(null);
      return;
    }
    setFile(files[0]);
    setErrorList([]);
  };

  const handleFileRejections = (fileRejections: FileRejection[]) => {
    const listFileErrorSize: string[] = [];
    const listFileErrorType: string[] = [];

    fileRejections.forEach((fileReject) => {
      const f = fileReject.file;
      if (f.size > MAX_SIZE) {
        listFileErrorSize.push(f.name);
      }
      const ext = f.name.split(".").pop()?.toLowerCase();
      const isValidMimeType = rules.acceptMimeTypes?.includes(f.type) ?? false;
      const isValidExtension = rules.acceptFileTypesExtension?.includes(`.${ext}`) ?? false;

      if (!isValidMimeType && !isValidExtension) {
        listFileErrorType.push(f.name);
      }
    });

    if (listFileErrorType.length > 0) {
      const extensions = getExtensionsOfMimeTypes([".csv", ".xlsm", ".xlsx"]);
      const errorType =
        `以下のファイル形式はアップロードできません。\n` +
        `${listFileErrorType.map((fileName) => `◦${fileName}`).join("\n")}\n` +
        `（アップロード可能なファイル形式は${extensions.join("、")}です。）`;
      toast.error(errorType);
      return;
    }

    if (listFileErrorSize.length > 0) {
      const maxSizeKB = MAX_SIZE / 1000;
      const errorSize =
        `以下のファイルはサイズ上限（${maxSizeKB}KB）を超えているため、アップロードできませんでした：\n` +
        `${listFileErrorSize.map((fileName) => `・${fileName}`).join("\n")}`;
      toast.error(errorSize);
    }
  };

  const getFileType = (f: File): FileType => {
    const extension = f.name.split(".").pop()?.toLowerCase();
    if (extension === "csv") return "csv";
    if (extension === "xlsx" || extension === "xlsm") return "xlsx";
    throw new Error(FILE_INCORRECT);
  };

  // Headers that can be omitted when company is pre-selected from modal
  const isCompanyPreSelected = selectedCompany && String(selectedCompany.value) !== String(DEFAULT_COMPANY_OPTION.value);
  const optionalHeaders = isCompanyPreSelected ? ["企業名"] : undefined;

  const readCSVFile = async (f: File): Promise<string[][]> => {
    try {
      const arrayBuffer = await f.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      const buffer = Buffer.from(uint8Array);
      if (!buffer) throw new Error(FILE_INCORRECT);
      const fileEncode = chardet.detect(buffer);
      if (!fileEncode) throw new Error(FILE_INCORRECT);
      const fileContent = iconv.decode(buffer, fileEncode);
      return new Promise((resolve, reject) => {
        Papa.parse(fileContent, {
          complete: (results) => {
            const rows = results.data as string[][];
            validateFileFormat(rows[0], optionalHeaders);
            resolve(rows);
          },
          error: (error: never) => reject(error),
          skipEmptyLines: false,
          dynamicTyping: false,
        });
      });
    } catch (e) {
      throw new Error(FILE_INCORRECT);
    }
  };

  const readXLSXFile = async (f: File): Promise<string[][]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const rows: [][] = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            defval: "",
            raw: false,
            range: "A1:BX209",
          });
          validateFileFormat(rows[0], optionalHeaders);
          resolve(rows);
        } catch (err) {
          reject(err);
        }
      };
      reader.readAsArrayBuffer(f);
    });
  };

  const readFileImport = async (f: File): Promise<string[][]> => {
    try {
      const fileType = getFileType(f);
      return fileType === "csv" ? await readCSVFile(f) : await readXLSXFile(f);
    } catch (e) {
      throw new Error(FILE_INCORRECT);
    }
  };

  const handleImportFile = async () => {
    if (!file) return;
    try {
      setIsSubmit(true);

      const rows: string[][] = await readFileImport(file);
      if (rows.length > MAX_LENGTH_ROWS) throw new Error(FILE_INCORRECT);

      const headerRow = rows[0].map((h) =>
        String(h)
          .replace(/^\uFEFF/, "")
          .trim(),
      );
      const keys: string[] = headerRow.map((h, i) => {
        if (h === "") return `_skip_${i}`;
        return headerToKey[h] || `_unknown_${i}`;
      });

      const results: string[][] = [];
      for (let i = 1; i < rows.length; i++) {
        const row = rows[i];
        if (row.length === 0) continue;
        if (row.every((cell) => String(cell).trim() === "")) continue;
        results.push(row);
      }

      const formattedResults: TJobRowExcel[] = results.map((row) => {
        const rowData = Object.fromEntries(
          keys.map((key, index) => {
            const value = row[index];
            const trimmed = typeof value === "string" ? sanitizeInput(value) : (value ?? "");
            return [key, trimmed];
          }),
        ) as TJobRowExcel;
        // If a real company selected in Combobox, override Excel column value and send companyId
        if (selectedCompany && String(selectedCompany.value) !== String(DEFAULT_COMPANY_OPTION.value)) {
          rowData.companyName = selectedCompany.label;
          rowData.companyId = Number(selectedCompany.value);
        }
        // Default empty 賞与/昇給 to 確認中 (pending)
        if (!rowData.bonus) rowData.bonus = "確認中";
        if (!rowData.salaryRaise) rowData.salaryRaise = "確認中";
        return rowData;
      });

      const { data: jobResponse } = await validateImportJobApi(formattedResults);
      modal.close();
      navigate(AppRoutes.jobImport, { state: { jobs: jobResponse } });
    } catch (err) {
      setIsSubmit(false);
      // err may be response.data (from interceptor) or raw AxiosError (from retry)
      const errorData = (err as AxiosError)?.response?.data ?? err;
      handleErrorImport(errorData);
    }
  };

  return (
    <Modal
      open={modal.visible}
      hideBackdrop={false}
      sx={{
        position: "fixed",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <ModalContainer width={920}>
        <Stack
          bgcolor={background.disable}
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          height="53px"
          sx={{
            borderTopLeftRadius: "8px",
            borderTopRightRadius: "8px",
            padding: "16px",
          }}
        >
          <Typography variant="body14Light" color={text.primary}>
            求人情報 | 一括インポート
          </Typography>
          <IconBox onClick={onClose} sx={{ p: 0 }}>
            <IconClose color={black[80]} fontSize={24} />
          </IconBox>
        </Stack>
        <Stack sx={{ padding: "32px 16px", gap: "24px" }}>
          {!isSubmit && (
            <Box mb={2}>
              <Typography variant="body14" fontWeight={600} mb={1}>
                企業を選択
              </Typography>
              <Combobox
                nonControl
                options={companyOptionsWithDefault}
                loading={isLoadingCompanies}
                placeholder="選択してください"
                value={selectedCompany?.value?.toString() ?? ""}
                onChange={(value) => {
                  if (!value) {
                    setCompanyError("企業を選択してください。");
                    setSelectedCompany(null);
                    return;
                  }
                  setCompanyError("");
                  if (String(value) === String(DEFAULT_COMPANY_OPTION.value)) {
                    setSelectedCompany(DEFAULT_COMPANY_OPTION);
                    return;
                  }
                  const found = companyOptions.find((o) => String(o.value) === String(value));
                  setSelectedCompany(found ?? null);
                }}
              />
              {companyError && (
                <Typography variant="cap12" color={text.error} mt={0.5}>
                  {companyError}
                </Typography>
              )}
            </Box>
          )}
          {!isSubmit && (
            <Box borderColor={divider.middle} borderRadius={rounded.sm}>
              <DropZone
                options={{ multiple: false }}
                onChange={handleChangeFiles}
                handleFileRejections={handleFileRejections}
                rules={{
                  maxFile: 1,
                  maxSize: MAX_SIZE,
                  acceptMimeTypes: rules.acceptMimeTypes,
                }}
                isShowErr={false}
              >
                <Typography variant="body14Light" whiteSpace="pre-wrap" textAlign="center">
                  アップロードするファイルをドラッグ&ドロップ
                </Typography>
                <Typography variant="body14Light" color={black[90]} whiteSpace="pre-wrap" textAlign="center">
                  （ファイル形式：csv、xlsx、xlsmに対応）
                </Typography>
                <Typography variant="body14Light" width={920} textAlign="center">
                  または
                </Typography>
                <Button type="button" variant="outline">
                  ファイルを選択する
                </Button>
                <Typography variant="cap12Light" color={black[70]} textAlign="start">
                  <ul>
                    <li>データ内に改行は含めないでください。</li>
                    <li>容量は最大10MBまでとなります。</li>
                  </ul>
                </Typography>
              </DropZone>
              {errorList.length > 0 && (
                <Stack
                  sx={{ mt: "32px", p: "8px", background: background.error, color: text.error, maxHeight: "160px", overflowY: "auto" }}
                  borderRadius={rounded.xs}
                >
                  {errorList.map((item, index) => (
                    <Typography key={index} component="p" variant="cap12">
                      {item}
                    </Typography>
                  ))}
                </Stack>
              )}
            </Box>
          )}
          {isSubmit && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", height: "150px" }}>
              <Box sx={{ height: 60 }}>
                <CircularProgress />
              </Box>
              <Typography variant="body14Light" textAlign="center">
                処理中
              </Typography>
            </Box>
          )}
        </Stack>
        <hr />
        <Stack flexDirection="row" gap="16px" justifyContent="space-between" alignItems="center" sx={{ padding: "16px 32px" }}>
          <Stack flexDirection="row" gap="16px" justifyContent="flex-start">
            <Button variant="ghost" onClick={onClose}>
              キャンセル
            </Button>
          </Stack>
          <Stack flexDirection="row" gap="16px" justifyContent="flex-end">
            {!isSubmit && (
              <Button
                onClick={() =>
                  downloadSample({
                    companySpecificIndustry: companySpecificIndustryOptions.filter((o) => !o.hidden),
                    businessClassification: businessClassificationOptions.filter((o) => !o.hidden),
                    referralOffice: referralOfficeOptions,
                    recruiter: recruiterOptions,
                    jobStatus: jobStatusOptions,
                    jobTag: jobTagOptions,
                    employmentType: employmentTypeOptions,
                    salaryType: salaryTypeOptions,
                    yesNoPending: yesNoPendingOptions,
                    japaneseLevelType: japaneseLevelTypeOptions,
                    recruitmentLocationType: recruitmentLocationTypeOptions,
                  })
                }
                variant="outline"
                startIcon={<FileDownloadIcon />}
              >
                テンプレートをダウンロード
              </Button>
            )}
            <Button
              onClick={handleImportFile}
              isLoading={isLoading}
              disabled={isSubmit || !file || errorList.length > 0}
              startIcon={<IconCompanyListImport />}
            >
              {isSubmit ? "ファイルを処理中..." : "インポート"}
            </Button>
          </Stack>
        </Stack>
      </ModalContainer>
    </Modal>
  );
};

const ModalContainer = styled(Box)`
  min-width: 417px;
  background-color: ${background.white};
  box-shadow: 0px 8px 24px 0px #00000026;
  border-radius: 8px;
  max-height: 90vh;
  outline: none;
  &:focus {
    outline: none;
  }
`;
